import React, { useCallback, useEffect, useState } from 'react';
import {
  MultiDropdown,
  useMultiDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import type AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import { useQueryClient } from '@tanstack/react-query';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import styled from 'styled-components';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching.filter_row.advanced_filters');

type Props = {
  filter: AdvancedFilter;
  onSelectedItemsChange: (changes: { selectedItems: Item[] }) => void;
};

const Wrapper = styled(Grid)`
  min-width: 0;

  > div {
    width: 100%;
  }

  /* Allow the MultiDropdown wrapper to shrink so the row's remove X stays visible. */
  > div > div[data-library='birch'] {
    min-width: 0;
    width: 100%;
  }

  .MuiFormControl-fullWidth {
    width: 100%;
    height: 40px;
  }

  .MuiInputBase-formControl {
    height: 40px;
  }
`;

const MultiSelectFilter = ({ filter, onSelectedItemsChange }: Props) => {
  const queryClient = useQueryClient();
  const [draftSelectedItems, setDraftSelectedItems] = useState<Item[]>(
    (filter.value as Item[]) || []
  );

  useEffect(() => {
    setDraftSelectedItems(((filter.value as Item[]) || []) as Item[]);
  }, [filter.value]);

  const fetchItems = useCallback(
    async (term: string) => {
      if (!filter.type) {
        return { items: [] };
      }

      const { value, valuesPath } = filter.type;

      if (!valuesPath) {
        return { items: [] };
      }

      const queryKey = ['filter', value, term];

      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const response = await standardRequestWithErrorFlash.get(valuesPath, {
            params: {
              term: term,
              q: term,
            },
          });

          return response.data.results;
        },
        staleTime: Infinity,
      });

      return { items };
    },
    [filter.type, queryClient]
  );

  const multiDropdownProps = useMultiDropdown({
    onSelectedItemsChange: (e) => setDraftSelectedItems(e.selectedItems),
    selectedItems: draftSelectedItems,
    fetchItems,
  });

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }

    onSelectedItemsChange({ selectedItems: draftSelectedItems });
  };

  return (
    <Wrapper>
      <div onBlur={handleBlur} tabIndex={-1}>
        <MultiDropdown dropdownProps={multiDropdownProps}>
          <MultiDropdown.Field
            placeholder={t('select')}
            ariaLabel={t('value')}
            condensedUnitName={t('selections')}
          />
          <MultiDropdown.Flyout disablePortal />
        </MultiDropdown>
      </div>
    </Wrapper>
  );
};

export default MultiSelectFilter;
